import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { RefreshCw, AlertTriangle, TrendingDown, Package, Target } from "lucide-react";

const fmt = (n, d = 1) => (n ?? 0).toLocaleString("en-ZA", { minimumFractionDigits: d, maximumFractionDigits: d });

const MODES = [
  { id: "last90",         label: "Last 90 days actual",  icon: TrendingDown },
  { id: "target_min",     label: "Target Min",           icon: Target },
  { id: "target_growth",  label: "Target Growth",        icon: Target },
  { id: "target_expand",  label: "Target Expand",        icon: Target },
];

const HORIZONS = [30, 60, 90, 180];

const STATUS_STYLES = {
  out:         { label: "OUT",       cls: "bg-destructive text-destructive-foreground border-destructive" },
  critical:    { label: "CRITICAL",  cls: "bg-destructive/15 text-destructive border-destructive/40" },
  low:         { label: "LOW",       cls: "bg-[hsl(31,65%,53%)]/15 text-[hsl(31,65%,38%)] border-[hsl(31,65%,53%)]/40" },
  warn:        { label: "WARN",      cls: "bg-amber-100 text-amber-900 border-amber-300" },
  ok:          { label: "OK",        cls: "bg-[hsl(132,18%,36%)]/10 text-[hsl(132,18%,28%)] border-[hsl(132,18%,36%)]/30" },
  no_velocity: { label: "—",         cls: "bg-muted text-muted-foreground border-border" },
};

export default function InventoryForecast() {
  const [mode, setMode] = useState("last90");
  const [horizon, setHorizon] = useState(90);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [refreshBusy, setRefreshBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setBusy(true); setError("");
    try {
      const r = await api.get(`/inventory/forecast?mode=${mode}&horizon_days=${horizon}`);
      setData(r.data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [mode, horizon]);

  const refreshCache = async () => {
    setRefreshBusy(true);
    try {
      await api.post("/inventory/forecast/refresh-cache");
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setRefreshBusy(false); }
  };

  const counts = data?.summary?.status_counts || {};
  const criticalCount = (counts.out || 0) + (counts.critical || 0);
  const warningCount = (counts.low || 0) + (counts.warn || 0);

  return (
    <div data-testid="inventory-forecast-page">
      <PageHeader
        eyebrow="Operations"
        title="Inventory Forecast"
        description="Per-origin green-bean velocity, days-of-stock, and reorder timing. Switch between actual last-90-day consumption and projected demand from the FY Min/Growth/Expand targets. Targets cached per FY (prior-FY data is static)."
        actions={
          <button onClick={refreshCache} disabled={refreshBusy} data-testid="forecast-refresh"
            className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded text-sm hover:bg-secondary disabled:opacity-60">
            <RefreshCw className={`w-4 h-4 ${refreshBusy ? "animate-spin" : ""}`} />
            {refreshBusy ? "Rebuilding cache…" : "Rebuild FY cache"}
          </button>
        }
      />

      {/* Mode + horizon selectors */}
      <div className="bg-card border border-border rounded p-4 mb-5">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[280px]">
            <p className="label-tiny mb-2">Forecast basis</p>
            <div className="flex flex-wrap gap-1.5" data-testid="forecast-modes">
              {MODES.map((m) => {
                const Ico = m.icon;
                const on = mode === m.id;
                return (
                  <button key={m.id} onClick={() => setMode(m.id)}
                    data-testid={`mode-${m.id}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold transition ${
                      on ? "bg-primary text-primary-foreground border-primary"
                          : "bg-card text-muted-foreground border-border hover:bg-secondary"
                    }`}>
                    <Ico className="w-3.5 h-3.5" /> {m.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <p className="label-tiny mb-2">Horizon (days)</p>
            <div className="inline-flex border border-border rounded overflow-hidden text-xs font-semibold" data-testid="horizon-selector">
              {HORIZONS.map((d, i) => (
                <button key={d} onClick={() => setHorizon(d)}
                  data-testid={`horizon-${d}`}
                  className={`px-3 py-1.5 ${horizon === d ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:bg-secondary"} ${i > 0 ? "border-l border-border" : ""}`}>
                  {d}d
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {error && <div className="bg-destructive/10 border border-destructive/30 rounded p-3 text-sm mb-4" data-testid="forecast-error">{error}</div>}

      {data && (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-testid="forecast-kpis">
            <KPI label="Total green on-hand" value={`${fmt(data.summary.total_on_hand_kg, 1)} kg`}
                 hint={`across ${data.origins.length} origins`} icon={Package} />
            <KPI label={`Green needed in ${horizon}d`} value={`${fmt(data.summary.total_green_needed_horizon_kg, 1)} kg`}
                 hint={data.origins[0]?.basis || ""} icon={TrendingDown}
                 accent={data.summary.total_green_needed_horizon_kg > 0 ? "warn" : "ok"} />
            <KPI label="Critical / out of stock" value={`${criticalCount}`}
                 hint={`${counts.out || 0} out · ${counts.critical || 0} <14d`}
                 icon={AlertTriangle} accent={criticalCount > 0 ? "critical" : "ok"} />
            <KPI label="Low / warning" value={`${warningCount}`}
                 hint={`${counts.low || 0} <30d · ${counts.warn || 0} <60d`}
                 icon={AlertTriangle} accent={warningCount > 0 ? "warn" : "ok"} />
          </div>

          {/* Cache age + FY context */}
          <div className="bg-secondary/40 border border-border rounded p-3 mb-5 text-xs flex flex-wrap items-center gap-x-6 gap-y-1 font-mono">
            <span>FY {data.fy_year}</span>
            <span>Prior-FY green: <strong className="text-foreground">{fmt(data.prior_fy_total_green_kg, 0)} kg</strong></span>
            <span>FY targets (green):
              {" "}<span className="text-muted-foreground">Min</span> {fmt(data.target_totals_green.min, 0)} ·
              {" "}<span className="text-muted-foreground">Growth</span> {fmt(data.target_totals_green.growth, 0)} ·
              {" "}<span className="text-muted-foreground">Expand</span> {fmt(data.target_totals_green.expand, 0)} kg
            </span>
            {data.cache_computed_at && (
              <span className="ml-auto text-muted-foreground">
                Cached {new Date(data.cache_computed_at).toLocaleString("en-ZA")}
              </span>
            )}
          </div>

          {/* Forecast table */}
          <div className="bg-card border border-border rounded overflow-x-auto">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="forecast-table">
                <thead className="bg-secondary">
                  <tr>
                    <th className="text-left p-3 label-tiny min-w-[180px]">Origin</th>
                    <th className="text-right p-3 label-tiny">On-hand (green)</th>
                    <th className="text-right p-3 label-tiny" title="Avg roast loss over last 180 days">Roast loss</th>
                    <th className="text-right p-3 label-tiny" title="Share of prior-FY green consumption (basis for target distribution)">Origin share</th>
                    <th className="text-right p-3 label-tiny">Velocity</th>
                    <th className="text-right p-3 label-tiny">Days of stock</th>
                    <th className="text-center p-3 label-tiny">Status</th>
                    <th className="text-right p-3 label-tiny">{`Green needed (${horizon}d)`}</th>
                    <th className="text-left p-3 label-tiny">Reorder by</th>
                  </tr>
                </thead>
                <tbody>
                  {busy && !data && <tr><td colSpan={9} className="p-8 text-center text-muted-foreground">Loading…</td></tr>}
                  {data.origins.length === 0 && <tr><td colSpan={9} className="p-8 text-center text-muted-foreground">No origins yet.</td></tr>}
                  {data.origins.map((o) => {
                    const status = STATUS_STYLES[o.status] || STATUS_STYLES.no_velocity;
                    const dim = o.status === "no_velocity";
                    return (
                      <tr key={o.origin_id}
                        className={`border-t border-border ${dim ? "opacity-60" : "hover:bg-secondary/40"}`}
                        data-testid={`forecast-row-${o.origin_id}`}>
                        <td className="p-3">
                          <p className="font-medium">{o.origin_name}</p>
                          {o.country && <p className="text-[10px] text-muted-foreground font-mono">{o.country}</p>}
                        </td>
                        <td className="p-3 text-right font-mono tabular-nums">{fmt(o.on_hand_kg, 1)} kg</td>
                        <td className="p-3 text-right font-mono tabular-nums text-muted-foreground">
                          {fmt(o.roast_loss_pct, 1)}%
                        </td>
                        <td className="p-3 text-right font-mono tabular-nums text-muted-foreground">
                          {fmt(o.share_pct_prior_fy, 1)}%
                        </td>
                        <td className="p-3 text-right font-mono tabular-nums">
                          {fmt(o.velocity_green_per_day, 2)} kg/d
                        </td>
                        <td className="p-3 text-right font-mono tabular-nums font-semibold">
                          {o.days_of_stock != null ? fmt(o.days_of_stock, 0) : "—"}
                        </td>
                        <td className="p-3 text-center">
                          <span className={`inline-block text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border font-bold ${status.cls}`}
                            data-testid={`forecast-status-${o.origin_id}`}>
                            {status.label}
                          </span>
                        </td>
                        <td className="p-3 text-right font-mono tabular-nums">
                          {o.green_kg_needed_horizon > 0 ? (
                            <span className="text-primary font-semibold">{fmt(o.green_kg_needed_horizon, 0)} kg</span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="p-3 text-xs font-mono text-muted-foreground">{o.reorder_by_date || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <p className="text-[11px] text-muted-foreground mt-3">
            Inventory is in green beans; targets are in roasted kg. Because the target % is a multiplier, it scales identically in green or roasted space — so prior-FY green consumption is used directly as the basis. Roast-loss % per origin is computed from the last 180 days and shown for context.
          </p>
        </>
      )}
    </div>
  );
}

function KPI({ label, value, hint, icon: Icon, accent }) {
  const tone = {
    ok:       "border-[hsl(132,18%,36%)]/30 bg-[hsl(132,18%,36%)]/5",
    warn:     "border-amber-300 bg-amber-50",
    critical: "border-destructive/40 bg-destructive/5",
  }[accent] || "border-border bg-card";
  return (
    <div className={`rounded p-4 border ${tone}`} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-").replace(/[()]/g, "")}`}>
      <div className="flex items-center justify-between mb-1.5">
        <p className="label-tiny">{label}</p>
        {Icon && <Icon className="w-4 h-4 text-muted-foreground" />}
      </div>
      <p className="font-display text-2xl font-bold tabular-nums">{value}</p>
      {hint && <p className="text-[10px] text-muted-foreground mt-1 font-mono truncate" title={hint}>{hint}</p>}
    </div>
  );
}
